import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { getAuth, onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { useNotification } from './NotificationContext';
import { getBusinesses, getBranches, clearTokenCache } from '../api';
import { clearAll as clearApiCache } from '../utils/apiCache';

// Create the context
export const AuthContext = createContext(null);

// Create a custom hook for easy access to the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Create the provider component
const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userBusinessId, setUserBusinessId] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [userContactId, setUserContactId] = useState(null);
    const [userBranchIds, setUserBranchIds] = useState([]);
    const auth = getAuth(); // This line will now work correctly
    const { showNotification } = useNotification();
    const sessionRef = useRef({ sessionId: null, unsubscribe: null, docPath: null });
    const sessionDisabledRef = useRef(process.env.REACT_APP_ENABLE_SESSION_GUARD === 'true' ? false : true);
    const sessionGuardEnabled = process.env.REACT_APP_ENABLE_SESSION_GUARD === 'true';

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const tokenResult = await firebaseUser.getIdTokenResult();
                const claims = tokenResult.claims;

                setUser(firebaseUser);
                const isAdminUser = !!claims.admin;
                setIsAdmin(isAdminUser);
                setUserBusinessId(claims.businessId || null);
                setUserContactId(claims.contactId || null);

                if (isAdminUser) {
                    setUserRole('Admin');
                } else {
                    setUserRole(claims.role || null);
                }

                // Warm the cache in the background — do not await, do not block loading state.
                getBusinesses().catch(() => {});
                if (!isAdminUser && claims.businessId) {
                    getBranches(claims.businessId).then(branches => {
                        if (claims.role === 'Employee' && claims.contactId && Array.isArray(branches)) {
                            const employeeBranchIds = branches
                                .filter(b => (b.employeeIds || []).includes(claims.contactId))
                                .map(b => b.branchId);
                            setUserBranchIds(employeeBranchIds);
                        }
                    }).catch(() => {});
                }

                if (!sessionDisabledRef.current && sessionGuardEnabled) {
                    const sessionId = generateSessionId();
                    const sessionDocRef = doc(firestore, 'sessions', firebaseUser.uid);
                    sessionRef.current.sessionId = sessionId;
                    sessionRef.current.docPath = sessionDocRef.path;

                    const registerSession = async () => {
                        try {
                            await setDoc(sessionDocRef, {
                                sessionId,
                                updatedAt: serverTimestamp(),
                            }, { merge: true });
                            return true;
                        } catch (error) {
                            console.error('Failed to register session:', error);
                            sessionDisabledRef.current = true;
                            return false;
                        }
                    };

                    const registered = await registerSession();

                    if (!registered) {
                        sessionRef.current.sessionId = null;
                        sessionRef.current.docPath = null;
                    } else {
                        if (sessionRef.current.unsubscribe) {
                            sessionRef.current.unsubscribe();
                        }

                        sessionRef.current.unsubscribe = onSnapshot(sessionDocRef, {
                            next: (snapshot) => {
                                const data = snapshot.data();
                                if (!data?.sessionId || !sessionRef.current.sessionId) return;
                                if (data.sessionId !== sessionRef.current.sessionId) {
                                    showNotification({
                                        message: 'You were signed out because this account was used on another device.',
                                        severity: 'warning',
                                        autoHideDuration: 6000,
                                    });
                                    sessionRef.current.sessionId = null;
                                    signOut(auth);
                                }
                            },
                            error: (error) => {
                                console.error('Session listener error:', error);
                                sessionDisabledRef.current = true;
                                sessionRef.current.sessionId = null;
                                if (sessionRef.current.unsubscribe) {
                                    sessionRef.current.unsubscribe();
                                    sessionRef.current.unsubscribe = null;
                                }
                            },
                        });
                    }
                }

            } else {
                setUser(null);
                setIsAdmin(false);
                setUserBusinessId(null);
                setUserRole(null);
                setUserContactId(null);
                setUserBranchIds([]);
                clearApiCache();
                clearTokenCache();
                if (sessionRef.current.unsubscribe) {
                    sessionRef.current.unsubscribe();
                    sessionRef.current.unsubscribe = null;
                }
                sessionRef.current.sessionId = null;
                sessionRef.current.docPath = null;
            }
            setLoading(false);
        });
        return unsubscribe;
    }, [auth, showNotification]);

    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } finally {
            if (sessionRef.current.unsubscribe) {
                sessionRef.current.unsubscribe();
                sessionRef.current.unsubscribe = null;
            }
            sessionRef.current.sessionId = null;
            sessionRef.current.docPath = null;
        }
    };

    // The value provided to all children components
    const value = {
        user,
        isAdmin,
        userBusinessId,
        userRole,
        loading,
        login,
        logout,
        userContactId,
        userBranchIds,
    };

    // Render children only when not loading to prevent flicker
    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
